<script setup lang="ts">
import type { VSelectButtonProps } from "./VSelectButton.types";
import { computed, ref } from "vue";
import { useSlidingIndicator } from "../../composables/SlidingIndicator/useSlidingIndicator";
import ACTooltip from "../ACTooltip/ACTooltip.vue";
import { formErrorMessageCva } from "../Form/form.cva";
import VIcon from "../Icon/VIcon.vue";
import { useFieldId } from "../useFieldId";
import { useRadioGroup } from "../useRadioGroup";
import { buttonContainerCva, buttonCva, chipCva, chipIconCva, chipsContainerCva, containerCva, indicatorCva, indicatorSurfaceCva, labelCva, optionContentCva, optionCountCva, optionIconCva, optionLabelCva, requiredCva } from "./VSelectButton.cva";

const {
  optionLabel = "name",
  optionValue = false,
  optionIcon = "icon",
  optionCount = undefined,
  label = "",
  disabled = false,
  layout = "is-column",
  labelPosition = "left",
  options,
  hasPerms = true,
  allowDeselect = false,
  multiple = false,
  tooltipValue = "tooltip",
  optionDisabled = "disabled",
  errorMessage = null,
  loading = false,
  wrap = false,
  block = false,
  size = "medium",
  appearance = "segmented",
  selectedStyle = "soft",
  invalid = false,
  required = false,
  id: rawId = undefined,
  ariaLabelledby = undefined,
  ariaLabel = undefined,
  ariaDescribedby = undefined,
  acTooltipContent = undefined,
} = defineProps<VSelectButtonProps>();

const isDisabled = computed(() => {
  return disabled || !hasPerms;
});

// While the value loads the options cannot be changed, without looking like a field the user may not edit.
const isInteractive = computed(() => !isDisabled.value && !loading);
// Only a radiogroup takes aria-invalid; a group of toggle buttons is linked to its error text alone.
const isInvalid = computed(() => invalid !== false || !!errorMessage);

const acTooltipPlacement = computed(() => (labelPosition === "top" ? "bottom" : "top"));

const modelValue = defineModel<any>({ required: true });

const id = useFieldId(() => rawId);
const labelId = computed(() => `${id.value}-label`);
const computedAriaLabelledby = computed(() => ariaLabelledby ?? (label ? labelId.value : undefined));
// A visible or external label wins, so the spoken name never differs from what is shown.
const computedAriaLabel = computed(() => (computedAriaLabelledby.value ? undefined : ariaLabel));
const errorId = computed(() => `${id.value}-error`);
const computedAriaDescribedby = computed(() => [ariaDescribedby, errorMessage ? errorId.value : undefined].filter(Boolean).join(" ") || undefined);

function getOptionValue(option: { [key: string]: any }) {
  if (typeof optionValue === "string") {
    return option[optionValue];
  }

  return option[optionLabel];
}

function updateSelection(option: { [key: string]: any }) {
  const value = getOptionValue(option);

  if (value === undefined || isOptionDisabled(option)) {
    return;
  }

  if (multiple) {
    const newValue = Array.isArray(modelValue.value) ? [...modelValue.value] : [];
    const index = newValue.indexOf(value);

    if (index >= 0) {
      newValue.splice(index, 1);
    } else {
      newValue.push(value);
    }

    modelValue.value = newValue;
    return;
  }

  // A radio stays checked when it is clicked again, unless the caller allows clearing the choice.
  if (modelValue.value === value) {
    if (allowDeselect) {
      modelValue.value = null;
    }
    return;
  }
  modelValue.value = value;
}

function isSelected(option: { [key: string]: any }) {
  const value = getOptionValue(option);

  return multiple
    ? Array.isArray(modelValue.value) && modelValue.value.includes(value)
    : modelValue.value === value;
}

function isOptionDisabled(option: { [key: string]: any }): boolean {
  if (!isInteractive.value) {
    return true;
  }
  if (typeof optionDisabled === "function") {
    return optionDisabled(option);
  }
  return !!option[optionDisabled];
}

// Moving through a single choice with the arrow keys selects an option, it never clears the choice.
const { setRadioRef, radioTabIndex, onRadioKeydown } = useRadioGroup(
  () => options.map(option => ({ checked: isSelected(option), disabled: isOptionDisabled(option) })),
  (index) => {
    const option = options[index];
    if (!option || isSelected(option)) {
      return;
    }
    updateSelection(option);
  },
);

// Several choices are toggle buttons in a group, one choice is a radio.
function optionAttributes(option: { [key: string]: any }, index: number) {
  if (multiple) {
    return { "aria-pressed": isSelected(option) };
  }
  return {
    "role": "radio",
    "aria-checked": isSelected(option),
    "tabindex": radioTabIndex(index),
  };
}

function onOptionKeydown(event: KeyboardEvent, index: number) {
  if (multiple) {
    return;
  }
  onRadioKeydown(event, index);
}
const buttonContainer = ref<HTMLElement | null>(null);

// Multi select needs one background per selected option, which a single pill cannot show; separate chips need none.
const { bounds: indicator, indicatorStyle, surfaceStyle, setOptionEl } = useSlidingIndicator({
  container: buttonContainer,
  activeKey: () => modelValue.value,
  enabled: () => !multiple && appearance !== "chips",
  watch: () => [options, options.length, wrap, block],
});

function optionClasses(option: { [key: string]: any }): string {
  if (appearance === "chips") {
    return chipCva({
      selected: isSelected(option),
      invalid: isInvalid.value,
      disabled: isOptionDisabled(option),
    });
  }
  return buttonCva({
    selected: isSelected(option),
    selectedStyle,
    indicator: Boolean(indicator.value),
    disabled: isOptionDisabled(option),
    block,
    size,
  });
}

function optionIconClasses(option: { [key: string]: any }): string {
  if (appearance === "chips") {
    return chipIconCva({ selected: isSelected(option) });
  }
  return optionIconCva({ size });
}

const classes = computed(() => {
  return {
    container: containerCva({
      labelPosition,
      block,
    }),
    indicator: indicatorCva(),
    indicatorSurface: indicatorSurfaceCva({ selectedStyle }),
    label: labelCva({
      labelPosition,
      layout,
    }),
    buttonContainer: appearance === "chips"
      ? chipsContainerCva({
          disabled: isDisabled.value,
          loading: loading && !isDisabled.value,
        })
      : buttonContainerCva({
          invalid: isInvalid.value,
          disabled: isDisabled.value,
          loading: loading && !isDisabled.value,
          wrap,
          block,
        }),
    optionContent: optionContentCva(),
    optionLabel: optionLabelCva(),
    error: formErrorMessageCva(),
    required: requiredCva({
      required: required !== false,
    }),
  };
});
</script>

<template>
  <div :class="classes.container">
    <label
      v-if="label"
      :id="labelId"
      :class="classes.label"
    >
      {{ label }}
      <span :class="classes.required">*</span>
    </label>
    <!-- self-start + w-fit stop floating-vue's reference wrapper from stretching to the full width of a
         top-label (flex-col) container, which would otherwise anchor the tooltip to the row's centre
         instead of over the buttons. The buttons stay put (they live in an inline-flex container). -->
    <ACTooltip
      type="edit_field"
      :class="block || appearance === 'chips' ? 'w-full' : 'w-fit self-start'"
      :allow-change="hasPerms"
      :custom-content="acTooltipContent"
      :placement="acTooltipPlacement"
    >
      <div
        :id="id"
        ref="buttonContainer"
        :class="classes.buttonContainer"
        :role="multiple ? 'group' : 'radiogroup'"
        :aria-labelledby="computedAriaLabelledby"
        :aria-label="computedAriaLabel"
        :aria-disabled="isDisabled || undefined"
        :aria-busy="loading || undefined"
        :aria-invalid="(!multiple && isInvalid) || undefined"
        :aria-describedby="computedAriaDescribedby"
      >
        <span
          v-if="indicator"
          aria-hidden="true"
          :class="classes.indicator"
          :style="indicatorStyle"
        >
          <span
            :class="classes.indicatorSurface"
            :style="surfaceStyle"
          />
        </span>
        <VTooltip
          v-for="(option, index) in options"
          :key="option[optionLabel]"
          :content="option[tooltipValue]"
          :disabled="!option[tooltipValue]"
          placement="top"
        >
          <template #reference>
            <button
              :ref="(element) => {
                setRadioRef(element, index); setOptionEl(getOptionValue(option), element);
              }"
              type="button"
              v-bind="optionAttributes(option, index)"
              :disabled="isOptionDisabled(option)"
              :class="optionClasses(option)"
              @click.prevent="updateSelection(option)"
              @keydown="onOptionKeydown($event, index)"
            >
              <slot v-if="$slots.value" name="value" :label="option[optionLabel]" :option="option" :selected="isSelected(option)" />
              <template v-else>
                <VIcon v-if="option[optionIcon]" :name="option[optionIcon]" :class="optionIconClasses(option)" aria-hidden="true" data-testid="select-button-icon" />
                <span :class="classes.optionContent" data-testid="select-button-content">
                  <span :class="classes.optionLabel" data-testid="select-button-label">{{ option[optionLabel] }}</span>
                  <span
                    v-if="optionCount && typeof option[optionCount] === 'number'"
                    :class="optionCountCva({ selected: isSelected(option) })"
                    data-testid="select-button-count"
                  >
                    {{ option[optionCount] }}
                  </span>
                </span>
              </template>
            </button>
          </template>
        </VTooltip>
      </div>
    </ACTooltip>
    <p v-if="errorMessage" :id="errorId" :class="classes.error">
      {{ errorMessage }}
    </p>
  </div>
</template>
